import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../../utils/api';
import { FolderKanban, LogOut, MapPin, Calendar, ChevronRight } from 'lucide-react';

export default function CustomerPortal() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const info = localStorage.getItem('customer_info');
    if (!token) { navigate('/customer/login'); return; }
    if (info) setCustomer(JSON.parse(info));

    api.customerGetProjects(token)
      .then(res => setProjects(res.data))
      .catch(() => { localStorage.removeItem('customer_token'); navigate('/customer/login'); })
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_info');
    navigate('/customer/login');
  };

  const statusLabel = (s) => {
    if (s === 'completed') return { text: 'Klart', cls: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' };
    if (s === 'active') return { text: 'Pågår', cls: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300' };
    if (s === 'paused') return { text: 'Pausat', cls: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300' };
    return { text: s, cls: 'bg-muted text-muted-foreground' };
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] dark:bg-[#0a0a0a]">
      <div className="w-8 h-8 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0a0a0a]" data-testid="customer-portal">
      <header className="bg-white dark:bg-card border-b border-border">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              <span className="text-[#E8613A]">Operio</span> Kundportal
            </span>
            {customer && <span className="text-sm text-muted-foreground ml-3">Hej, {customer.name || customer.email}</span>}
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="customer-logout-btn">
            <LogOut size={14} /> Logga ut
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <h2 className="text-xl font-bold text-foreground mb-6">Dina projekt</h2>

        {projects.length === 0 ? (
          <div className="bg-white dark:bg-card border border-border rounded-xl p-12 text-center">
            <FolderKanban size={40} className="text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">Inga projekt tilldelade ännu.</p>
            <p className="text-sm text-muted-foreground mt-1">Ditt entreprenadföretag bjuder in dig när projekt startas.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map(p => {
              const st = statusLabel(p.status);
              const progress = p.progress_percent || 0;
              return (
                <Link
                  key={p.id}
                  to={`/customer/portal/project/${p.id}`}
                  className="bg-white dark:bg-card border border-border rounded-xl p-5 hover:border-[#E8613A]/40 transition-colors group"
                  data-testid={`customer-project-${p.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-base font-bold text-foreground group-hover:text-[#E8613A] transition-colors">{p.name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${st.cls}`}>{st.text}</span>
                  </div>
                  {p.location && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2"><MapPin size={10} /> {p.location}</p>
                  )}
                  {(p.start_date && p.start_date !== 'None' || p.end_date && p.end_date !== 'None') && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mb-3">
                      <Calendar size={10} /> {p.start_date || '—'} — {p.end_date || '—'}
                    </p>
                  )}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-[#E8613A] rounded-full transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="text-sm font-bold text-foreground">{progress}%</span>
                    <ChevronRight size={16} className="text-muted-foreground group-hover:text-[#E8613A]" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
